import React from 'react';
import { motion } from 'framer-motion';
import { Plus, Trash2 } from 'lucide-react';
import { useHomepageConfig } from '@/hooks/useHomepageConfig';
import { useEditMode } from '@/context/EditModeContext';
import { Editable } from '@/components/edit/Editable';
import { EditableImage } from '@/components/edit/EditableImage';

const DEFAULT_LOGOS = [
  'https://www.casasamba.com/wp-content/uploads/2022/01/client-carousel1.jpg',
  'https://www.casasamba.com/wp-content/uploads/2022/01/client-carousel1a.jpg',
  'https://www.casasamba.com/wp-content/uploads/2022/01/client-carousel11.jpg',
  'https://www.casasamba.com/wp-content/uploads/2022/01/client-carousel12.jpg',
  'https://www.casasamba.com/wp-content/uploads/2022/01/client-carousel13.jpg',
  'https://www.casasamba.com/wp-content/uploads/2022/01/client-carousel14.jpg',
];

const LogoTile = ({ item, index, enabled, onRemove }) => (
  <div className="relative flex-shrink-0 w-40 h-24 bg-white rounded-xl flex items-center justify-center p-3 border border-border/30">
    <EditableImage
      path={`clients.${index}.logo`}
      fallback={item.logo}
      alt={item.name || 'Client logo'}
      className="w-full h-full"
      imgClassName="max-w-full max-h-full object-contain"
    />
    {enabled && (
      <button
        type="button"
        onClick={() => onRemove(index)}
        className="absolute -top-2 -right-2 z-20 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center shadow-lg hover:bg-red-600"
        data-testid={`remove-client-${index}`}
        data-edit-safe
        title="Remove"
      >
        <Trash2 className="w-3 h-3" />
      </button>
    )}
  </div>
);

const LogoRow = ({ items, offset, reverse, enabled, onRemove }) => (
  <div className="overflow-hidden relative">
    <div
      className="flex gap-6 w-max"
      style={{ animation: enabled ? 'none' : `cs-marquee${reverse ? '-rev' : ''} 45s linear infinite` }}
    >
      {(enabled ? items : [...items, ...items]).map((item, idx) => (
        <LogoTile
          key={idx}
          item={item}
          index={offset + (idx % items.length)}
          enabled={enabled}
          onRemove={onRemove}
        />
      ))}
    </div>
  </div>
);

export const HomeClients = () => {
  const cfg = useHomepageConfig();
  const { enabled, getValue, update } = useEditMode();
  const source = enabled ? (getValue('clients') || cfg?.clients) : cfg?.clients;
  const clients = (Array.isArray(source) && source.length)
    ? source
    : DEFAULT_LOGOS.map(u => ({ logo: u, name: 'Client' }));

  const addClient = () => {
    const next = [...(getValue('clients') || cfg?.clients || []), { logo: '', name: 'Client' }];
    update('clients', next);
  };
  const removeClient = (idx) => {
    const cur = getValue('clients') || cfg?.clients || [];
    update('clients', cur.filter((_, i) => i !== idx));
  };

  const half = Math.ceil(clients.length / 2);
  const rowA = clients.slice(0, half);
  const rowB = clients.slice(half);

  return (
    <section className="relative py-20 md:py-24 px-0 bg-black/25 backdrop-blur-md border-y border-white/10" data-testid="clients-section">
      <style>{`
        @keyframes cs-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        @keyframes cs-marquee-rev { from { transform: translateX(-50%); } to { transform: translateX(0); } }
      `}</style>
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <Editable
            as="span"
            path="clientsSubheading"
            fallback={cfg?.clientsSubheading || '36+ Years of Trusted Partnerships'}
            className="text-primary font-bold uppercase tracking-widest text-sm mb-4 block"
          />
          <Editable
            as="h2"
            path="clientsHeading"
            fallback={cfg?.clientsHeading || 'Some of Our Clients'}
            className="text-3xl md:text-5xl font-black text-foreground"
          />
        </motion.div>
        {enabled && (
          <div className="flex justify-center mb-8">
            <button
              type="button"
              onClick={addClient}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground font-bold text-sm hover:opacity-90"
              data-testid="add-client-btn"
              data-edit-safe
            >
              <Plus className="w-4 h-4" /> Add client logo
            </button>
          </div>
        )}
      </div>
      <div className="space-y-6">
        <LogoRow items={rowA} offset={0} enabled={enabled} onRemove={removeClient} />
        <LogoRow items={rowB} offset={half} reverse enabled={enabled} onRemove={removeClient} />
      </div>
    </section>
  );
};

export default HomeClients;
